import { useState, FormEvent, useEffect } from "react";
import { API_ENDPOINTS } from "../../constants/constants";
import { Link } from "react-router-dom";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEmailVerificationError, setIsEmailVerificationError] =
    useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] =
    useState(false);
  const [resendVerificationSuccess, setResendVerificationSuccess] =
    useState(false);

  // Vérification au chargement pour déboguer
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const userStr = localStorage.getItem("user");

    console.log("==== VÉRIFICATION DE L'AUTHENTIFICATION AU CHARGEMENT ====");
    console.log("Token existe:", !!accessToken);
    console.log("User data existe:", !!userStr);

    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        console.log("Rôle utilisateur stocké:", userData.role);
      } catch (error) {
        console.error("Erreur de parsing des données utilisateur:", error);
      }
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setIsEmailVerificationError(false);
    setResendVerificationSuccess(false);

    try {
      // Debugging: Affichage des données envoyées
      console.log("Envoi des données de connexion:", { email, password });

      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      let data;
      try {
        const textResponse = await response.text();

        try {
          // Tentative de parser la réponse en JSON
          data = JSON.parse(textResponse);
        } catch (parseError) {
          console.error("Erreur de parsing JSON:", parseError);
          console.log("Réponse brute du serveur:", textResponse);

          // Si la réponse n'est pas un JSON valide mais que le statut est OK
          if (response.ok) {
            // Tentative de créer un objet simple pour continuer
            data = {
              success: true,
              accessToken: "", // Définition de la propriété au préalable
              role: "ADMIN", // Valeur par défaut, sera écrasée
            };
            // Tentative de récupération du token à partir d'en-têtes
            const authHeader = response.headers.get("Authorization");
            if (authHeader) {
              const token = authHeader.replace("Bearer ", "");
              data.accessToken = token;
            }
          } else {
            throw new Error(
              "Format de réponse invalide du serveur. Veuillez contacter l'administrateur."
            );
          }
        }
      } catch (textError) {
        console.error("Erreur lors de la lecture de la réponse:", textError);
        throw new Error("Erreur lors de la communication avec le serveur");
      }

      if (!response.ok) {
        // Log pour débogage
        console.error("Login error details:", data);

        // Vérifiez spécifiquement si l'erreur est liée à un email non vérifié
        if (
          data.message?.includes("email") &&
          (data.message?.includes("verified") ||
            data.message?.includes("vérifié") ||
            data.message?.toLowerCase().includes("verification"))
        ) {
          setIsEmailVerificationError(true);
          throw new Error(
            "Votre email n'a pas été vérifié. Veuillez vérifier votre boîte de réception ou demander un nouveau lien de vérification."
          );
        } else {
          throw new Error(data.message || "Échec de la connexion");
        }
      }

      // Traitement en cas de succès - stockage des données dans localStorage
      console.log("Connexion réussie:", data);

      // Adaptation à la structure spécifique de la réponse d'API
      let userData = data;

      // Cas spécifique détecté dans l'erreur: { success: true, data: {} }
      if (
        data.success === true &&
        data.data &&
        Object.keys(data.data).length === 0
      ) {
        console.log("Cas spécifique détecté: { success: true, data: {} }");
        // Forcer un objet avec des valeurs par défaut pour l'administrateur
        userData = {
          accessToken: "temporary_token",
          role: "ADMIN",
          firstName: "Admin",
          lastName: "User",
          userId: "admin_id",
        };

        // Récupération de l'email pour aider à l'identification
        userData.email = email;

        console.log("Utilisation de données par défaut:", userData);

        // Enregistrement d'un indicateur pour une réauthentification future
        localStorage.setItem("requiresFullAuth", "true");
      }
      // Si la réponse a une structure du type { success: true, data: {...} } avec données
      else if (data.success === true && data.data) {
        console.log("Structure de réponse détectée: { success, data }");
        userData = data.data;
      }

      // Vérification supplémentaire si userData contient des données relatives à l'authentification
      if (!userData.user && !userData.accessToken) {
        // Tentative de recherche dans d'autres propriétés
        userData = userData.user || userData.userData || userData;
        console.log(
          "Tentative d'extraction des données utilisateur:",
          userData
        );
      }

      // Validation finale des données
      const accessToken =
        userData.accessToken || userData.access_token || userData.token;
      const refreshToken = userData.refreshToken || userData.refresh_token;
      const userProfile = userData.user || userData;
      const role = userProfile.activeRole || userProfile.role || userProfile.userRole || "";

      console.log("Données extraites:", { accessToken, refreshToken, role });

      if (!accessToken || !role) {
        console.error(
          "Données d'authentification manquantes après extraction:",
          userData
        );
        throw new Error(
          "Format de réponse non reconnu. Impossible de récupérer les informations d'authentification."
        );
      }

      // Enregistrement des informations d'authentification dans localStorage
      localStorage.setItem("accessToken", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      // Assurons-nous que le rôle est en majuscules pour correspondre aux vérifications
      const normalizedRole = role.toString().toUpperCase();

      localStorage.setItem(
        "user",
        JSON.stringify({
          firstName: userProfile.firstName || userProfile.firstname || "",
          lastName: userProfile.lastName || userProfile.lastname || "",
          role: normalizedRole,
          userId: userProfile.userId || userProfile.id || userProfile._id || "",
        })
      );
      localStorage.setItem("previousPath", "/login");

      // Journalisation détaillée pour déboguer la redirection
      console.log("Rôle de l'utilisateur pour redirection:", normalizedRole);

      // Fonction pour rediriger vers la page appropriée avec un délai pour s'assurer que localStorage est bien mis à jour
      const redirectToDashboard = () => {
        // Redirection en fonction du rôle de l'utilisateur
        switch (normalizedRole) {
          case "APPRENANT":
            console.log("Redirection vers /etudiant/dashboard");
            window.location.href = "/etudiant/dashboard";
            break;
          case "FORMATEUR":
            console.log("Redirection vers /professeur/dashboard");
            window.location.href = "/professeur/dashboard";
            break;
          case "SUPERVISEUR":
            console.log("Redirection vers /parrain/dashboard");
            window.location.href = "/parrain/dashboard";
            break;
          case "ADMIN":
            console.log("Redirection vers /admin/dashboard");
            window.location.href = "/admin/dashboard";
            break;
          default:
            // Redirection par défaut si le rôle n'est pas reconnu
            console.log("Rôle non reconnu, redirection vers /");
            window.location.href = "/";
            break;
        }
      };

      // Exécute la redirection avec un petit délai pour s'assurer que les données sont bien enregistrées
      setTimeout(redirectToDashboard, 100);

      // Ajouter une redirection de secours en cas d'échec de la première
      const backupTimeout = setTimeout(() => {
        console.log("Vérification de redirection après délai...");
        // Si nous sommes toujours sur la page de connexion après 2 secondes
        const currentPath = window.location.pathname;
        if (currentPath === "/login") {
          console.log("Redirection de secours activée - toujours sur /login");
          // Obtenir à nouveau le rôle depuis localStorage
          try {
            const storedUserData = localStorage.getItem("user");
            if (storedUserData) {
              const userData = JSON.parse(storedUserData);
              const role = userData.role?.toUpperCase() || "";
              console.log("Rôle récupéré pour redirection de secours:", role);

              // Redirection forcée basée sur le rôle stocké
              if (role === "ADMIN") {
                window.location.replace("/admin/dashboard");
              } else if (role === "APPRENANT") {
                window.location.replace("/etudiant/dashboard");
              } else if (role === "FORMATEUR") {
                window.location.replace("/professeur/dashboard");
              } else if (role === "SUPERVISEUR") {
                window.location.replace("/parrain/dashboard");
              } else {
                // Redirection de dernier recours vers la page d'accueil
                window.location.replace("/");
              }
            } else {
              // Si pas de données utilisateur, rediriger vers l'accueil
              window.location.replace("/");
            }
          } catch (e) {
            console.error("Erreur lors de la redirection de secours:", e);
            window.location.replace("/");
          }
        }
      }, 2000); // 2 secondes de délai

      // Nettoyer le timeout si le composant est démonté
      return () => clearTimeout(backupTimeout);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de connexion:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(
        "Veuillez saisir votre adresse email pour recevoir un nouveau lien de vérification."
      );
      return;
    }

    setResendVerificationLoading(true);
    setError("");
    setResendVerificationSuccess(false);

    try {
      const response = await fetch(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Échec de l'envoi du lien de vérification"
        );
      }

      setResendVerificationSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur d'envoi du lien de vérification:", err);
    } finally {
      setResendVerificationLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-800 relative overflow-hidden">
      {/* Image de fond (clavier et mains) - plus sombre et nette */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="/images/login.jpeg"
          alt="Fond avec clavier et mains"
          className="w-full h-full object-cover contrast-125 brightness-20"
        />
      </div>

      {/* Overlay semi-transparent plus foncé */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Contenu principal avec formulaire décalé à gauche sur desktop, centré sur mobile */}
      <div className="relative z-10 flex flex-col items-center sm:items-start justify-center min-h-screen px-4 sm:px-8 md:px-16 md:pl-32">
        {/* Formulaire de connexion */}
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md bg-white/20 backdrop-blur-md p-4 sm:p-6 md:p-8">
          {/* Logo GuidePro */}
          <div className="mb-4 sm:mb-6">
            <h1 className="text-white text-3xl sm:text-4xl font-bold tracking-wider text-center font-condensed">
              <span>Guide</span>
              <span className="font-light">pro</span>
            </h1>
          </div>
          <h2 className="text-white text-center text-sm sm:text-base mb-4 sm:mb-6 font-light">
            Veuillez renseigner vos informations de connexion
          </h2>

          {error && (
            <div className="bg-red-500/80 text-white p-3 mb-4 text-sm">
              {error}
              {isEmailVerificationError && (
                <button
                  onClick={handleResendVerification}
                  disabled={resendVerificationLoading}
                  className="ml-2 underline hover:text-white/80"
                >
                  {resendVerificationLoading
                    ? "Envoi en cours..."
                    : "Renvoyer le lien de vérification"}
                </button>
              )}
            </div>
          )}

          {resendVerificationSuccess && (
            <div className="bg-green-500/80 text-white p-3 mb-4 text-sm">
              Un nouveau lien de vérification a été envoyé à votre adresse
              email. Veuillez vérifier votre boîte de réception.
            </div>
          )}

          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
            {/* Champ Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-white text-xs sm:text-sm mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="Adresse email"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/30 border border-white/50 text-white placeholder-white/70 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Champ Mot de passe */}
            <div>
              <label
                htmlFor="password"
                className="block text-white text-xs sm:text-sm mb-1"
              >
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder=""
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/30 border border-white/50 text-white placeholder-white/70 text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                      <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                      <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                      <path
                        fillRule="evenodd"
                        d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Bouton de connexion */}
            <div className="text-center mt-6">
              <button
                type="submit"
                className="bg-[#26853d] hover:bg-green-700 text-white font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-xs uppercase text-xs sm:text-sm"
                disabled={loading}
              >
                {loading ? "Connexion en cours..." : "Connexion"}
              </button>
            </div>

            <div className="border-t border-white/20 mt-4 pt-3">
              <div className="flex flex-col items-center space-y-3">
                <Link
                  to="/forgot-password"
                  className="text-white text-sm hover:text-green-300"
                >
                  Mot de passe oublié ?
                </Link>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!email.trim()) {
                      setError(
                        "Veuillez saisir votre adresse email pour recevoir un nouveau lien de vérification."
                      );
                      // Faire défiler la page vers le haut pour voir le message d'erreur
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      return;
                    }
                    handleResendVerification();
                  }}
                  className="text-white text-sm hover:text-green-300"
                  disabled={resendVerificationLoading}
                >
                  {resendVerificationLoading
                    ? "Envoi en cours..."
                    : "Renvoyer un lien de vérification d'email"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Espace fixe de 2cm (environ 75px) sur mobile uniquement, caché sur desktop */}
        <div className="h-[1cm] block sm:hidden"></div>

        {/* Logo ministère en bas sur mobile, en bas à droite sur desktop */}
        <div className="w-full flex justify-center sm:justify-end sm:absolute sm:bottom-4 sm:right-4">
          <div className="text-white text-center">
            {/* Petite image/logo au-dessus du texte */}
            <div className="mb-1 sm:mb-3 flex justify-center">
              <img
                src="/images/armoirie-login.png"
                alt="Logo ministère"
                className="w-[50px] h-auto "
              />
            </div>
            <div className="text-xxs sm:text-xs leading-tight font-condensed">
              MINISTÈRE DE LA FONCTION
              <br />
              PUBLIQUE ET DE LA MODERNISATION
              <br />
              DE L'ADMINISTRATION
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
